import { useState, useRef, useEffect } from 'react'
import { ChevronDown, Bot, Calendar } from 'lucide-react'

interface Assistant {
  id: string
  name: string
  is_active: boolean
  google_email?: string | null
}

interface AssistantSelectorProps {
  assistants: Assistant[]
  selectedId: string | null
  onChange: (assistantId: string | null) => void
  loading?: boolean
}

export function AssistantSelector({
  assistants,
  selectedId,
  onChange,
  loading
}: AssistantSelectorProps) {
  const [isOpen, setIsOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const selectedAssistant = assistants.find(a => a.id === selectedId)

  if (assistants.length === 0) {
    return null
  }

  if (assistants.length === 1) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700">
        <div className="w-6 h-6 rounded-full bg-emerald-500/20 flex items-center justify-center">
          <Bot className="w-3.5 h-3.5 text-emerald-400" />
        </div>
        <span className="text-sm text-white max-w-[150px] truncate">
          {assistants[0].name}
        </span>
      </div>
    )
  }

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => !loading && setIsOpen(!isOpen)}
        disabled={loading}
        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 hover:border-zinc-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {selectedAssistant ? (
          <>
            <div className="w-6 h-6 rounded-full bg-emerald-500/20 flex items-center justify-center">
              <Bot className="w-3.5 h-3.5 text-emerald-400" />
            </div>
            <span className="text-sm text-white max-w-[150px] truncate">
              {selectedAssistant.name}
            </span>
          </>
        ) : (
          <>
            <div className="w-6 h-6 rounded-full bg-zinc-700 flex items-center justify-center">
              <Calendar className="w-3.5 h-3.5 text-zinc-400" />
            </div>
            <span className="text-sm text-zinc-300">Selecione um assistente</span>
          </>
        )}
        <ChevronDown className={`w-4 h-4 text-zinc-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-1 w-64 max-h-80 overflow-auto rounded-lg bg-zinc-800 border border-zinc-700 shadow-xl">
          <div className="px-3 py-2 border-b border-zinc-700">
            <p className="text-xs text-zinc-500 uppercase tracking-wider">Assistentes Conectados</p>
          </div>

          {assistants.map(assistant => (
            <button
              key={assistant.id}
              type="button"
              onClick={() => {
                onChange(assistant.id)
                setIsOpen(false)
              }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-zinc-700/50 transition-colors ${
                selectedId === assistant.id ? 'bg-zinc-700/30' : ''
              }`}
            >
              <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                <Bot className="w-4 h-4 text-emerald-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-white truncate">{assistant.name}</p>
                {assistant.google_email && (
                  <p className="text-xs text-zinc-500 truncate">{assistant.google_email}</p>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
